<template>
  <div class="container relative flex flex-col">
    <div>
      <tx-alert :show="hasError" type="error" :text="errorMessage" dismissible />
      <div v-if="comments.length || (!comments.length && discussion.createdBy === userStore.userProfile.Id)" class="flex items-center justify-between w-ful">
        <tx-switch v-if="comments.length" v-model="resolveProp" class="w-full pt-1" type="inline" :label="t('general.resolve')" :disabled="isReadOnly" @change="onResolveChange" />
        <tx-button
          v-if="discussion.createdBy === userStore.userProfile.Id" v-tooltip="t('general.delete')" class="ml-auto" type="icon" faicon="fa-light fa-trash-can" :disabled="resolveProp || isReadOnly"
          @click="onDelete"
        />
      </div>
    </div>
    <div v-if="comments.length" class="flex-grow overflow-y-auto max-h-36">
      <div v-for="comment in comments" :key="comment.id" class="flex pb-3">
        <div class="w-6 h-6 pt-px mr-1 text-sm text-center text-white border-2 rounded-full bg-grey-dark">
          {{ comment.firstName.substring(0, 1) + comment.lastName.substring(0, 1) }}
        </div>
        <div class="flex flex-col flex-1 pr-1">
          <div class="flex text-xxs">
            <div class="flex-1">
              {{ comment.firstName }} {{ comment.lastName }}
            </div>
            <div class="flex flex-col items-end">
              <use-time-ago v-slot="{ timeAgo }" :time="new Date(comment.date)">
                {{ timeAgo }}
              </use-time-ago>
            </div>
          </div>
          <div class="flex">
            <div v-sanitize-html="comment.content" class="mr-2 text-sm flex-1" />
            <font-awesome-icon
              v-if="comment.userId === userStore.userProfile.Id && !(resolveProp || isReadOnly)" v-tooltip="t('general.edit')"
              icon="fa-light fa-pencil" class="text-[10px] mt-1 cursor-pointer hover:text-gray-500 items-end" @click="onEditContent(comment)"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-between py-1">
      <div class="flex flex-1 pr-1">
        <mentionable-input
          ref="refMentionableInput" v-model="commentContent" :placeholder="t('general.comments')"
          clearable :disabled="resolveProp || sending || isReadOnly" insert-space :map-insert="(item) => item.label"
          :items="mentionableUsersAndGroups"
        >
          <template #item="{ item }">
            <div class="p-1 border rounded-lg border-grey bg-slate-50 hover:bg-primary hover:text-on-primary">
              <div class="flex">
                <div
                  v-if="item.type"
                  class="w-5 h-5 pt-px mr-1 text-xs text-center text-white border-2 rounded-full bg-grey-dark"
                >
                  {{ item.type.substring(0, 1).toUpperCase() }}
                </div>
                <div class="flex-1 text-sm truncate">
                  {{ item.label }}
                </div>
              </div>
              <div class="ml-1 text-sm truncate">
                {{ item.subTitle }}
              </div>
            </div>
          </template>
        </mentionable-input>
      </div>
      <div class="flex flex-col justify-end">
        <div class="flex flex-col justify-end">
          <tx-button
            v-if="editingCommentId"
            v-tooltip="t('general.cancel')"
            type="icon"
            faicon="fa-light fa-xmark"
            class="mr-1"
            @click="onCancelEdit"
          />
          <tx-button
            v-tooltip="editingCommentId ? t('general.update') : t('general.save')"
            type="icon"
            :faicon="editingCommentId ? 'fa-light fa-save' : 'fa-light fa-paper-plane-top'"
            :disabled="!commentContent.length || resolveProp || isReadOnly"
            :loading="sending"
            @click="editingCommentId ? onUpdate() : onSend()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UseTimeAgo } from '@vueuse/components'
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import type WbDiscussionIcon from '../services/discussionIcon'
import type Whiteboard from '../services/whiteboard'
import TxAlert from '@/shared/components/TxAlert.vue'
import TxSwitch from '@/shared/components/TxSwitch.vue'
import TxButton from '@/shared/components/TxButton.vue'
import MentionableInput from '@/shared/components/MentionableInput.vue'
import { sendMentionNotifications } from '@/api/t1/whiteboard'
import useErrorMessage from '@/shared/composables/errorMessage'
import { useUserStore } from '@/store/userData'
import { whiteboardConstants } from '@/models/constants'

const props = defineProps<{
  discussion: WbDiscussionIcon
  whiteboardId: number
  whiteboard?: Whiteboard
  permission: WhiteboardUserPermissionType
  isLocked?: boolean
}>()

const emit = defineEmits<{
  (e: 'delete'): void
}>()

const { t } = useI18n()
const userStore = useUserStore()
const { errorMessage, hasError } = useErrorMessage()

const refMentionableInput = ref<InstanceType<typeof MentionableInput>>()
const sending = ref(false)
const resolveProp = ref(props.discussion.resolved)
const comments = ref<IDiscussionComment[]>([])
const commentContent = ref('')
const editingCommentId = ref<string | null>(null)

const isReadOnly = computed(() => props.permission === whiteboardConstants.userPermissions.read || props.isLocked)
const mentionableUsersAndGroups = computed<IMentionItem[]>(() => {
  let result: IMentionItem[] = []
  if (props.whiteboard) {
    result = props.whiteboard.mentionableUsersAndGroups.value
  }
  return result
})

function onResolveChange() {
  props.discussion.setProp('resolved', { resolved: resolveProp.value })
}

function onDelete() {
  emit('delete')
}

function onEditContent(comment: IDiscussionComment) {
  editingCommentId.value = comment.id
  commentContent.value = comment.content
  nextTick(() => {
    refMentionableInput.value?.setContent(comment.content)
  })
}

async function onUpdate() {
  if (commentContent.value.length && editingCommentId.value && refMentionableInput.value) {
    sending.value = true
    try {
      const mentionedItems = refMentionableInput.value.getMentionedItems()

      const success = props.discussion.updateComment(
        editingCommentId.value,
        commentContent.value,
        mentionedItems,
      )

      if (success) {
        // Send mention notifications
        if (mentionedItems.length && userStore.activeCatalog) {
          const currentUrl = window.location.href
          const baseUrl = currentUrl.split('/oid')[0]
          const urlWithNewObjId = `${baseUrl}/oid/${props.discussion.id}`

          const userIds = new Set<number>()
          const accountGroupIds = new Set<number>()
          mentionedItems.forEach((item) => {
            if (item.type === 'user') {
              userIds.add(item.value)
            }
            else if (item.type === 'group') {
              accountGroupIds.add(item.value)
            }
          })

          await sendMentionNotifications(
            userStore.activeCatalog.CatalogCode,
            props.whiteboardId,
            {
              Users: [...userIds],
              AccountGroups: [...accountGroupIds],
              Url: urlWithNewObjId,
              Content: commentContent.value,
            },
          )
        }

        editingCommentId.value = null
        commentContent.value = ''
        refMentionableInput.value.clear()
      }
    }
    catch (e) {
      console.error(e)
    }
    finally {
      sending.value = false
    }
  }
}

function onCancelEdit() {
  editingCommentId.value = null
  commentContent.value = ''
  nextTick(() => {
    refMentionableInput.value?.setContent(commentContent.value)
  })
}

async function onSend() {
  if (commentContent.value.length && refMentionableInput.value) {
    const mentionedItems = refMentionableInput.value.getMentionedItems()
    // if previous oid part of the url remove it
    const currentUrl = window.location.href
    const baseUrl = currentUrl.split('/oid')[0]
    const urlWithNewObjId = `${baseUrl}/oid/${props.discussion.id}`
    if (mentionedItems.length && userStore.activeCatalog) {
      sending.value = true
      const userIds = new Set<number>()
      const accountGroupIds = new Set<number>()
      mentionedItems.forEach((item) => {
        if (item.type === 'user') {
          userIds.add(item.value)
        }
        else if (item.type === 'group') {
          accountGroupIds.add(item.value)
        }
      })
      try {
        await sendMentionNotifications(userStore.activeCatalog.CatalogCode, props.whiteboardId, { Users: [...userIds], AccountGroups: [...accountGroupIds], Url: urlWithNewObjId, Content: commentContent.value })
      }
      catch (e) {
        console.error(e)
      }
      sending.value = false
    }
    props.discussion.addComment(commentContent.value, mentionedItems, userStore.userProfile, false)
    refMentionableInput.value.clear()
  }
}

function load() {
  resolveProp.value = props.discussion.resolved || false
  comments.value = props.discussion.comments
}

load()

watch(() => props.discussion, load)

function handleObjectModified(e) {
  if (e.target?.id === props.discussion.id) {
    resolveProp.value = e.target.resolved || false
    comments.value = e.target.comments
  }
}

onMounted(() => {
  props.whiteboard?.on('object-modified-server', handleObjectModified)
})

onUnmounted(() => {
  props.whiteboard?.off('object-modified-server', handleObjectModified)
})
</script>
